import { NO, UIView, YES } from "uicore-ts"
import { RHUsageEvent, RHUsageEventKind, RHUsageEventRuntime, RHUsageViewDescriptor } from "../Core/RHUsageEventRuntime"


export type RHTestRecordedEventKind = RHUsageEventKind

export interface RHTestRecordedEvent extends RHUsageEvent {}

export interface RHTestViewDescriptor extends RHUsageViewDescriptor {}

export interface RHTestRuntimeInterface {
    beginRecording: () => void
    finishRecording: () => RHTestRecordedEvent[]
    clearRecording: () => void
    recordedEvents: () => RHTestRecordedEvent[]
    viewDescriptors: () => RHTestViewDescriptor[]
    registerMilestone: (message: string) => void
    sendControlEventToTarget: (target: string, eventKey: string) => void
    fillTargetWithText: (target: string, text: string) => void
}

declare global {
    interface Window {
        RHTestRuntime?: RHTestRuntimeInterface
    }
}

export class RHTestRuntime {
    
    static _isInstalled = NO
    static _isRecording = NO
    static _recordedEvents: RHTestRecordedEvent[] = []
    static _eventSink = (event: RHUsageEvent) => RHTestRuntime._usageEventWasEmitted(event)
    
    static install() {
        
        if (RHTestRuntime._isInstalled) {
            return
        }
        
        RHTestRuntime._isInstalled = YES
        RHUsageEventRuntime.install()
        RHUsageEventRuntime.addEventSink(RHTestRuntime._eventSink)
        
        window.RHTestRuntime = {
            beginRecording: () => RHTestRuntime.beginRecording(),
            finishRecording: () => RHTestRuntime.finishRecording(),
            clearRecording: () => RHTestRuntime.clearRecording(),
            recordedEvents: () => RHTestRuntime.recordedEvents(),
            viewDescriptors: () => RHTestRuntime.viewDescriptors(),
            registerMilestone: message => RHTestRuntime.registerMilestone(message),
            sendControlEventToTarget: (target, eventKey) => RHTestRuntime.sendControlEventToTarget(target, eventKey),
            fillTargetWithText: (target, text) => RHTestRuntime.fillTargetWithText(target, text)
        }
        
    }
    
    static registerViewWithTestIdentifier(view: UIView, testIdentifier: string) {
        
        RHUsageEventRuntime.registerViewWithUsageIdentifier(view, testIdentifier)
        
    }
    
    static beginRecording() {
        
        RHTestRuntime.clearRecording()
        RHUsageEventRuntime.resetSanitizedJSONSession()
        RHTestRuntime._isRecording = YES
        RHTestRuntime.registerMilestone("Recording started")
        
    }
    
    static finishRecording(): RHTestRecordedEvent[] {
        
        RHTestRuntime.registerMilestone("Recording finished")
        RHTestRuntime._isRecording = NO
        return RHTestRuntime.recordedEvents()
        
    }
    
    static clearRecording() {
        
        RHTestRuntime._recordedEvents = []
        
    }
    
    static recordedEvents(): RHTestRecordedEvent[] {
        
        return RHTestRuntime._recordedEvents.slice()
        
    }
    
    static registerMilestone(message: string) {
        
        RHUsageEventRuntime.registerMilestone(message)
        
    }
    
    static viewDescriptors(): RHTestViewDescriptor[] {
        
        return RHUsageEventRuntime.viewDescriptors()
        
    }
    
    static sendControlEventToTarget(target: string, eventKey: string) {
        
        RHUsageEventRuntime.sendControlEventToTarget(target, eventKey)
        
    }
    
    static fillTargetWithText(target: string, text: string) {
        
        RHUsageEventRuntime.fillTargetWithText(target, text)
        
    }
    
    static _usageEventWasEmitted(event: RHUsageEvent) {
        
        if (!RHTestRuntime._eventShouldBeRecorded(event)) {
            return
        }
        
        RHTestRuntime._appendEvent(event)
        
    }
    
    static _eventShouldBeRecorded(event: RHUsageEvent): boolean {
        
        if (RHTestRuntime._isRecording !== YES) {
            return NO
        }
        if (event.kind !== "socket") {
            return YES
        }
        if (event.functionName === "ButtonWasPressed" || event.functionName === "RouteDidChange") {
            return NO
        }
        
        return YES
        
    }
    
    static _appendEvent(event: RHTestRecordedEvent) {
        
        RHTestRuntime._recordedEvents.push({ ...event })
        
    }
    
}
